import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

// 🧰 Utilitários — ferramentinhas do dia a dia. Cada card é independente;
// pra adicionar um novo, é só criar o componente e listar no return.

// 🔢 Cola telefone/CNPJ/CPF/CEP formatado → fica só o número, já copiado
function SoNumeros() {
  const [valor, setValor] = useState('');
  const [copiado, setCopiado] = useState(false);

  // Autolimpeza: 5s após a última colagem/digitação o campo esvazia sozinho
  // (o número limpo já está no clipboard, então nada se perde)
  useEffect(() => {
    if (!valor) return;
    const t = setTimeout(() => { setValor(''); setCopiado(false); }, 5000);
    return () => clearTimeout(t);
  }, [valor]);

  const limpo = valor.replace(/\D/g, '');
  const tipo = (() => {
    if (!limpo) return '';
    if (limpo.length === 8) return 'CEP';
    if (limpo.length === 10) return 'Telefone fixo';
    if (limpo.length === 11) return 'CPF ou celular';
    if (limpo.length === 14) return 'CNPJ';
    return `${limpo.length} dígito${limpo.length !== 1 ? 's' : ''}`;
  })();

  const copiar = texto => {
    if (!texto) return;
    navigator.clipboard?.writeText(texto).then(() => {
      setCopiado(true);
      setTimeout(() => setCopiado(false), 1600);
    }).catch(() => {});
  };

  return (
    <div style={{ background: '#fff', border: '1px solid #dbe2ea', borderRadius: 10, boxShadow: '0 1px 3px rgba(10,22,40,.06)', padding: '12px 16px', maxWidth: 520 }}>
      <div style={{ fontSize: 13, fontWeight: 700, color: '#0a1628', marginBottom: 3 }}>🔢 Só números</div>
      <div style={{ fontSize: 11.5, color: '#6b7280', marginBottom: 8 }}>
        Cole telefone, CNPJ, CPF ou CEP com pontos e traços — sai só o número, já copiado pro clipboard.
      </div>
      <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
        <input
          value={valor}
          onChange={e => { setValor(e.target.value.replace(/\D/g, '')); setCopiado(false); }}
          onPaste={e => {
            e.preventDefault();
            const t = (e.clipboardData?.getData('text') || '').replace(/\D/g, '');
            setValor(t);
            copiar(t);
          }}
          placeholder="(83) 99999-9999 · 12.345.678/0001-90 · 123.456.789-00"
          style={{ flex: 1, padding: '6px 10px', border: '1px solid #d1d5db', borderRadius: 6, fontSize: 14, fontFamily: 'monospace', letterSpacing: 0.5, outline: 'none' }} />
        <button onClick={() => copiar(limpo)} disabled={!limpo}
          style={{ padding: '6px 12px', background: copiado ? '#16a34a' : '#0a1628', color: '#fff', border: 'none', borderRadius: 6, fontSize: 12, fontWeight: 600, cursor: limpo ? 'pointer' : 'default', opacity: limpo ? 1 : 0.4, whiteSpace: 'nowrap' }}>
          {copiado ? '✓ Copiado' : '📋 Copiar'}
        </button>
        {valor && (
          <button onClick={() => { setValor(''); setCopiado(false); }} title="Limpar"
            style={{ background: 'none', border: 'none', fontSize: 14, cursor: 'pointer', color: '#9ca3af', padding: 2 }}>✕</button>
        )}
      </div>
      {limpo && <div style={{ fontSize: 11, color: '#6b7280', marginTop: 5 }}>Parece <strong>{tipo}</strong> · {limpo.length} dígitos · <span style={{ color: '#9ca3af' }}>limpa sozinho em 5s</span></div>}
    </div>
  );
}

// ⏱️ Contador de prazo — projeta o vencimento a partir de uma data (padrão:
// hoje) + prazo em dias corridos, na regra do art. 224 da LPI: exclui o dia
// do começo, inclui o do vencimento. Caindo em fim de semana, mostra o
// próximo dia útil (feriados não entram na conta — conferir no calendário).
const DIAS_SEMANA = ['domingo', 'segunda-feira', 'terça-feira', 'quarta-feira', 'quinta-feira', 'sexta-feira', 'sábado'];
const PRAZOS_COMUNS = [5, 15, 30, 60, 90];

function hojeISO() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

function ContadorPrazo() {
  const [inicio, setInicio] = useState(hojeISO());
  const [prazo, setPrazo] = useState('60');
  const [copiado, setCopiado] = useState(false);

  const dias = Math.max(0, parseInt(prazo, 10) || 0);
  const base = inicio ? new Date(inicio + 'T12:00:00') : null;
  let venc = null, util = null;
  if (base && !isNaN(base) && dias > 0) {
    venc = new Date(base);
    venc.setDate(venc.getDate() + dias);
    util = new Date(venc);
    while (util.getDay() === 0 || util.getDay() === 6) util.setDate(util.getDate() + 1);
  }
  const fmt = d => d ? d.toLocaleDateString('pt-BR') : '';
  const fimDeSemana = venc && (venc.getDay() === 0 || venc.getDay() === 6);

  // Contagem regressiva a partir de HOJE até o vencimento útil
  const hoje = new Date(hojeISO() + 'T12:00:00');
  const restam = util ? Math.round((util - hoje) / 86400000) : null;
  const chipRestam = restam === null ? null
    : restam > 0 ? { txt: `faltam ${restam} dia${restam !== 1 ? 's' : ''}`, bg: '#f0fdf4', bd: '#bbf7d0', cor: '#15803d' }
    : restam === 0 ? { txt: 'vence HOJE', bg: '#fffbeb', bd: '#fde68a', cor: '#b45309' }
    : { txt: `venceu há ${-restam} dia${restam !== -1 ? 's' : ''}`, bg: '#fef2f2', bd: '#fecaca', cor: '#b91c1c' };

  const resumo = util
    ? `Prazo de ${dias} dias a partir de ${fmt(base)} → vence em ${fmt(venc)} (${DIAS_SEMANA[venc.getDay()]})${fimDeSemana ? ` — prorroga pro próximo dia útil: ${fmt(util)} (${DIAS_SEMANA[util.getDay()]})` : ''}`
    : '';

  const copiar = () => {
    if (!resumo) return;
    navigator.clipboard?.writeText(resumo).then(() => {
      setCopiado(true);
      setTimeout(() => setCopiado(false), 1600);
    }).catch(() => {});
  };

  const inputStyle = { padding: '6px 10px', border: '1px solid #d1d5db', borderRadius: 6, fontSize: 14, outline: 'none' };
  return (
    <div style={{ background: '#fff', border: '1px solid #dbe2ea', borderRadius: 10, boxShadow: '0 1px 3px rgba(10,22,40,.06)', padding: '12px 16px', maxWidth: 520 }}>
      <div style={{ fontSize: 13, fontWeight: 700, color: '#0a1628', marginBottom: 3 }}>⏱️ Contador de prazo</div>
      <div style={{ fontSize: 11.5, color: '#6b7280', marginBottom: 8 }}>
        Projeta o vencimento: data da publicação + prazo em dias corridos (exclui o dia do começo, inclui o do vencimento — art. 224 da LPI).
      </div>
      <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap' }}>
        <input type="date" value={inicio} onChange={e => setInicio(e.target.value)} style={inputStyle} />
        {inicio !== hojeISO() && (
          <button onClick={() => setInicio(hojeISO())} title="Voltar pra data de hoje"
            style={{ padding: '6px 10px', background: '#fff', color: '#374151', border: '1px solid #d1d5db', borderRadius: 6, fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>Hoje</button>
        )}
        <span style={{ fontSize: 13, color: '#6b7280' }}>+</span>
        <input type="number" min="1" max="9999" value={prazo} onChange={e => setPrazo(e.target.value)}
          style={{ ...inputStyle, width: 64, textAlign: 'center', fontWeight: 700 }} />
        <span style={{ fontSize: 13, color: '#6b7280' }}>dias</span>
        <div style={{ display: 'flex', gap: 4 }}>
          {PRAZOS_COMUNS.map(p => (
            <button key={p} onClick={() => setPrazo(String(p))}
              style={{ padding: '4px 8px', borderRadius: 5, fontSize: 11.5, fontWeight: dias === p ? 700 : 500, border: '1px solid', borderColor: dias === p ? '#0a1628' : '#d1d5db', background: dias === p ? '#0a1628' : '#fff', color: dias === p ? '#fff' : '#374151', cursor: 'pointer' }}>{p}</button>
          ))}
        </div>
      </div>
      {util && (
        <div style={{ marginTop: 10, background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 8, padding: '10px 12px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
            <div>
              <div style={{ fontSize: 10.5, color: '#6b7280', fontWeight: 700, textTransform: 'uppercase' }}>Vencimento</div>
              <div style={{ fontSize: 22, fontWeight: 800, color: '#0a1628' }}>{fmt(venc)}</div>
              <div style={{ fontSize: 11.5, color: '#6b7280' }}>{DIAS_SEMANA[venc.getDay()]}</div>
            </div>
            {chipRestam && (
              <span style={{ fontSize: 12, fontWeight: 700, color: chipRestam.cor, background: chipRestam.bg, border: `1px solid ${chipRestam.bd}`, borderRadius: 20, padding: '3px 12px' }}>{chipRestam.txt}</span>
            )}
            <button onClick={copiar} title="Copia o resumo do prazo pra colar no WhatsApp"
              style={{ marginLeft: 'auto', padding: '6px 12px', background: copiado ? '#16a34a' : '#0a1628', color: '#fff', border: 'none', borderRadius: 6, fontSize: 12, fontWeight: 600, cursor: 'pointer', whiteSpace: 'nowrap' }}>
              {copiado ? '✓ Copiado' : '📋 Copiar'}
            </button>
          </div>
          {fimDeSemana && (
            <div style={{ marginTop: 8, fontSize: 12, color: '#b45309', background: '#fffbeb', border: '1px solid #fde68a', borderRadius: 6, padding: '6px 10px' }}>
              ⚠️ Cai num <strong>{DIAS_SEMANA[venc.getDay()]}</strong> — prorroga pro próximo dia útil: <strong>{fmt(util)}</strong> ({DIAS_SEMANA[util.getDay()]}).
            </div>
          )}
          <div style={{ marginTop: 6, fontSize: 10.5, color: '#9ca3af' }}>Feriados não entram na conta — caindo perto de um, confira o expediente do INPI.</div>
        </div>
      )}
    </div>
  );
}

// 💳 Dashboard de consumo de créditos de IA (só o Vagner) — lê o contador
// interno (/ia-consumo/resumo) e ESTIMA o custo em US$ pelos preços de tabela
// por modelo; o valor oficial é sempre o do console da Anthropic.
function precoDoModelo(model) {
  const m = String(model || '').toLowerCase();
  if (m.includes('opus')) return { in: 5, out: 25 };
  if (m.includes('haiku')) return { in: 1, out: 5 };
  return { in: 3, out: 15 }; // sonnet e desconhecidos
}
function custoUsd(linha, model) {
  const p = precoDoModelo(model);
  return (linha.input_tokens * p.in + linha.output_tokens * p.out
    + (linha.cache_read_tokens || 0) * p.in * 0.1
    + (linha.cache_write_tokens || 0) * p.in * 1.25) / 1e6;
}
const fmtK = (n) => n >= 1e6 ? (n / 1e6).toFixed(1) + 'M' : n >= 1000 ? (n / 1000).toFixed(1) + 'k' : String(n || 0);
const NOME_MODULO = { chat_dono: '👑 Clau (seu WhatsApp/painel)', clau_leads: '💬 Clau dos leads', clau_extracao: '🔎 Extração de leads', agente_vendas: '🤖 Agente de Vendas', minuta: '⚖️ Minuta de recurso', briefing: '📰 Briefing', rascunho: '✨ Formatar rascunho' };

function CardConsumoIA() {
  const [aberto, setAberto] = useState(false);
  const [dias, setDias] = useState(30);
  const [dados, setDados] = useState(null);

  useEffect(() => {
    if (!aberto) return;
    setDados(null);
    fetch(`/ia-consumo/resumo?dias=${dias}`, { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } })
      .then(r => r.json()).then(setDados).catch(() => setDados({ erro: true }));
  }, [aberto, dias]);

  const custoTotal = dados?.por_modelo
    ? Object.entries(dados.por_modelo).reduce((s, [model, linha]) => s + custoUsd(linha, model), 0)
    : 0;
  // economia do cache: quanto custaria se o cache lido fosse input cheio
  const economia = dados?.por_modelo
    ? Object.entries(dados.por_modelo).reduce((s, [model, l]) => s + ((l.cache_read_tokens || 0) * precoDoModelo(model).in * 0.9) / 1e6, 0)
    : 0;
  const diasOrdenados = dados?.por_dia ? Object.entries(dados.por_dia).sort((a, b) => a[0].localeCompare(b[0])).slice(-14) : [];
  const maxDia = Math.max(1, ...diasOrdenados.map(([, v]) => v));
  const totalTokensModulos = dados?.por_modulo
    ? Object.values(dados.por_modulo).reduce((s, m) => s + m.input_tokens + m.output_tokens + (m.cache_read_tokens || 0), 0) : 0;

  return (
    <>
      <div onClick={() => setAberto(true)}
        style={{ background: '#fff', border: '1px solid #dbe2ea', borderRadius: 10, boxShadow: '0 1px 3px rgba(10,22,40,.06)', padding: '12px 16px', maxWidth: 520, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 12 }}>
        <div style={{ fontSize: 22 }}>💳</div>
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: 13, fontWeight: 700, color: '#0a1628', marginBottom: 3 }}>Consumo de créditos de IA</div>
          <div style={{ fontSize: 11.5, color: '#6b7280' }}>Dashboard: gasto estimado, tokens por módulo, economia do cache e tendência.</div>
        </div>
        <div style={{ fontSize: 14, color: '#9ca3af' }}>›</div>
      </div>

      {aberto && (
        <div onClick={() => setAberto(false)} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,.55)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1300, padding: 16 }}>
          <div onClick={e => e.stopPropagation()} style={{ background: '#fff', borderRadius: 10, width: 680, maxWidth: '96vw', maxHeight: '92vh', overflowY: 'auto', padding: 18 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12 }}>
              <div style={{ fontSize: 15, fontWeight: 800, color: '#0a1628', flex: 1 }}>💳 Consumo de créditos de IA</div>
              {[7, 30, 90].map(d => (
                <button key={d} onClick={() => setDias(d)}
                  style={{ padding: '3px 10px', borderRadius: 4, fontSize: 12, fontWeight: dias === d ? 700 : 500, border: '1px solid', borderColor: dias === d ? '#0a1628' : '#d1d5db', background: dias === d ? '#0a1628' : '#fff', color: dias === d ? '#fff' : '#374151', cursor: 'pointer' }}>{d}d</button>
              ))}
              <button onClick={() => setAberto(false)} style={{ background: 'none', border: 'none', fontSize: 18, cursor: 'pointer', color: '#6b7280' }}>✕</button>
            </div>

            {!dados ? (
              <div style={{ color: '#6b7280', fontSize: 13, padding: 16 }}>Carregando…</div>
            ) : dados.erro ? (
              <div style={{ color: '#b91c1c', fontSize: 13, padding: 16 }}>Erro ao carregar o consumo.</div>
            ) : (
              <>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: 8, marginBottom: 14 }}>
                  <div style={{ background: '#0a1628', borderRadius: 8, padding: '10px 12px' }}>
                    <div style={{ fontSize: 10.5, color: '#94a3b8', fontWeight: 700, textTransform: 'uppercase' }}>Custo estimado</div>
                    <div style={{ fontSize: 22, fontWeight: 800, color: '#fff' }}>US$ {custoTotal.toFixed(2)}</div>
                    <div style={{ fontSize: 10.5, color: '#94a3b8' }}>últimos {dados.dias} dias</div>
                  </div>
                  <div style={{ background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: 8, padding: '10px 12px' }}>
                    <div style={{ fontSize: 10.5, color: '#15803d', fontWeight: 700, textTransform: 'uppercase' }}>Cache economizou</div>
                    <div style={{ fontSize: 22, fontWeight: 800, color: '#15803d' }}>US$ {economia.toFixed(2)}</div>
                    <div style={{ fontSize: 10.5, color: '#15803d' }}>{fmtK(dados.cache_read_tokens)} tokens lidos a 10%</div>
                  </div>
                  <div style={{ background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 8, padding: '10px 12px' }}>
                    <div style={{ fontSize: 10.5, color: '#6b7280', fontWeight: 700, textTransform: 'uppercase' }}>Chamadas</div>
                    <div style={{ fontSize: 22, fontWeight: 800, color: '#0a1628' }}>{dados.chamadas}</div>
                    <div style={{ fontSize: 10.5, color: '#6b7280' }}>{fmtK(dados.input_tokens)} in · {fmtK(dados.output_tokens)} out</div>
                  </div>
                </div>

                {diasOrdenados.length > 1 && (
                  <div style={{ marginBottom: 14 }}>
                    <div style={{ fontSize: 11, fontWeight: 700, color: '#6b7280', textTransform: 'uppercase', marginBottom: 4 }}>Tendência (tokens/dia)</div>
                    <div style={{ display: 'flex', alignItems: 'flex-end', gap: 3, height: 46 }}>
                      {diasOrdenados.map(([dia, v]) => (
                        <div key={dia} title={`${dia.slice(8)}/${dia.slice(5, 7)}: ${fmtK(v)} tokens`}
                          style={{ flex: 1, background: '#1d4ed8', opacity: .85, borderRadius: '2px 2px 0 0', height: `${Math.max(8, (v / maxDia) * 100)}%` }} />
                      ))}
                    </div>
                  </div>
                )}

                <div style={{ fontSize: 11, fontWeight: 700, color: '#6b7280', textTransform: 'uppercase', marginBottom: 4 }}>Por módulo</div>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12.5, marginBottom: 12 }}>
                  <thead><tr style={{ background: '#0a1628' }}>{['Módulo', 'Chamadas', 'Tokens', '% do uso'].map((h, i) => <th key={h} style={{ padding: '4px 8px', color: '#fff', fontSize: 11, textAlign: i ? 'right' : 'left' }}>{h}</th>)}</tr></thead>
                  <tbody>
                    {Object.entries(dados.por_modulo || {}).sort((a, b) => (b[1].input_tokens + b[1].output_tokens) - (a[1].input_tokens + a[1].output_tokens)).map(([mod, m], i) => {
                      const tk = m.input_tokens + m.output_tokens + (m.cache_read_tokens || 0);
                      return (
                        <tr key={mod} style={{ background: i % 2 ? '#fafafa' : '#fff', borderBottom: '1px solid #eee' }}>
                          <td style={{ padding: '4px 8px' }}>{NOME_MODULO[mod] || mod}</td>
                          <td style={{ padding: '4px 8px', textAlign: 'right' }}>{m.chamadas}</td>
                          <td style={{ padding: '4px 8px', textAlign: 'right' }}>{fmtK(tk)}</td>
                          <td style={{ padding: '4px 8px', textAlign: 'right', fontWeight: 700 }}>{totalTokensModulos ? Math.round(tk / totalTokensModulos * 100) : 0}%</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>

                <div style={{ fontSize: 11, fontWeight: 700, color: '#6b7280', textTransform: 'uppercase', marginBottom: 4 }}>Custo por modelo (estimado)</div>
                {Object.entries(dados.por_modelo || {}).sort((a, b) => custoUsd(b[1], b[0]) - custoUsd(a[1], a[0])).map(([model, l]) => (
                  <div key={model} style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12.5, padding: '3px 2px', borderBottom: '1px dashed #eee' }}>
                    <span style={{ color: '#374151' }}>{model || '(sem modelo)'}</span>
                    <b style={{ color: '#0a1628' }}>US$ {custoUsd(l, model).toFixed(2)}</b>
                  </div>
                ))}
                <div style={{ fontSize: 11, color: '#9ca3af', marginTop: 10 }}>
                  Estimativa pelos preços de tabela (Opus 5/25 · Sonnet 3/15 · Haiku 1/5 por milhão; cache lido a 10%, gravado a 125%). O valor oficial é o do{' '}
                  <a href="https://console.anthropic.com/settings/usage" target="_blank" rel="noreferrer" style={{ color: '#1d4ed8' }}>console da Anthropic</a> — crédito entra em{' '}
                  <a href="https://console.anthropic.com/settings/billing" target="_blank" rel="noreferrer" style={{ color: '#1d4ed8' }}>Billing</a>.
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </>
  );
}

// 📝 Atalho pras Anotações — a página continua em /anotacoes, só mudou de porta de entrada
function CardAnotacoes() {
  const navigate = useNavigate();
  return (
    <div onClick={() => navigate('/anotacoes')}
      style={{ background: '#fff', border: '1px solid #dbe2ea', borderRadius: 10, boxShadow: '0 1px 3px rgba(10,22,40,.06)', padding: '12px 16px', maxWidth: 520, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 12 }}>
      <div style={{ fontSize: 22 }}>📝</div>
      <div style={{ flex: 1 }}>
        <div style={{ fontSize: 13, fontWeight: 700, color: '#0a1628', marginBottom: 3 }}>Anotações</div>
        <div style={{ fontSize: 11.5, color: '#6b7280' }}>Seu bloco de notas rápido — só você vê.</div>
      </div>
      <div style={{ fontSize: 14, color: '#9ca3af' }}>›</div>
    </div>
  );
}

function TranscreverAudio() {
  const [arquivo, setArquivo] = useState(null);
  const [template, setTemplate] = useState('generico');
  const [rodando, setRodando] = useState(false);
  const [resultado, setResultado] = useState(null);
  const [erro, setErro] = useState('');
  const [exportando, setExportando] = useState('');
  const [acoesSel, setAcoesSel] = useState({});
  const [criandoTarefas, setCriandoTarefas] = useState(false);
  const [buscaCli, setBuscaCli] = useState('');
  const [opcoesCli, setOpcoesCli] = useState([]);
  const [clienteSel, setClienteSel] = useState(null);
  const tk = () => localStorage.getItem('token');

  useEffect(() => {
    if (buscaCli.trim().length < 3 || clienteSel) { setOpcoesCli([]); return; }
    const t = setTimeout(() => {
      fetch(`/clientes?busca=${encodeURIComponent(buscaCli.trim())}`, { headers: { Authorization: `Bearer ${tk()}` } })
        .then(r => r.json())
        .then(d => setOpcoesCli((Array.isArray(d) ? d : d.clientes || []).slice(0, 6)))
        .catch(() => {});
    }, 350);
    return () => clearTimeout(t);
  }, [buscaCli, clienteSel]);

  async function transcrever() {
    if (!arquivo) return setErro('Escolha um arquivo de áudio primeiro.');
    setRodando(true); setErro(''); setResultado(null);
    try {
      const fd = new FormData();
      fd.append('arquivo', arquivo);
      fd.append('template', template);
      const r = await fetch('/assistente/transcrever-audio', { method: 'POST', headers: { Authorization: `Bearer ${tk()}` }, body: fd });
      const d = await r.json();
      if (!r.ok) throw new Error(d.erro || 'Falha na transcrição');
      setResultado(d);
      setAcoesSel(Object.fromEntries((d.acoes || []).map((_, i) => [i, true])));
    } catch (e) { setErro(e.message); }
    setRodando(false);
  }

  const tituloDoc = () => `Transcrição — ${(arquivo?.name || 'áudio').replace(/\.[^.]+$/, '')}`;

  async function exportar(formato, modo) {
    if (!resultado) return;
    setExportando(formato + (modo || ''));
    try {
      const corpo = { formato, titulo: tituloDoc(), transcricao: resultado.transcricao, resumo: resultado.resumo };
      if (modo === 'wa') corpo.whatsapp = true;
      if (modo === 'cliente') { corpo.salvar_cliente = true; corpo.cliente_id = clienteSel?._id; }
      const r = await fetch('/assistente/transcricao-doc', {
        method: 'POST', headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${tk()}` },
        body: JSON.stringify(corpo),
      });
      if (modo) {
        const d = await r.json();
        if (!r.ok || d.erro) throw new Error(d.erro || 'falhou');
        alert(modo === 'wa' ? '📲 Enviado no seu WhatsApp!' : `💾 PDF salvo na aba Upload de ${clienteSel?.nome}!`);
      } else {
        if (!r.ok) { const d = await r.json(); throw new Error(d.erro || 'falha'); }
        const blob = await r.blob();
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url; a.download = `transcricao.${formato === 'docx' ? 'docx' : 'pdf'}`;
        document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
      }
    } catch (e) { alert('Falhou: ' + e.message); }
    setExportando('');
  }

  async function criarTarefas() {
    const escolhidas = (resultado?.acoes || []).filter((_, i) => acoesSel[i]);
    if (!escolhidas.length) return alert('Marque ao menos uma ação.');
    setCriandoTarefas(true);
    let ok = 0;
    for (const a of escolhidas) {
      try {
        const r = await fetch('/agenda', {
          method: 'POST', headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${tk()}` },
          body: JSON.stringify({
            descricao: `🎤 ${a.descricao}`,
            data_agendada: a.data ? `${a.data}T09:00` : '',
            destinado_a: localStorage.getItem('nomeUsuario') || '',
            subagenda: 'administrativo',
            cliente_id: clienteSel?._id || '',
            cliente_nome: clienteSel?.nome || '',
          }),
        });
        if (r.ok) ok++;
      } catch { /* segue */ }
    }
    setCriandoTarefas(false);
    alert(`✅ ${ok} tarefa(s) criada(s) na Agenda${clienteSel ? ` (vinculadas a ${clienteSel.nome})` : ''}.`);
  }

  const btn = (bg, cor) => ({ background: bg, color: cor || '#fff', border: 'none', borderRadius: 6, padding: '7px 14px', fontSize: 12.5, fontWeight: 700, cursor: 'pointer' });
  return (
    <div style={{ background: '#fff', border: '1px solid #dbe2ea', borderRadius: 10, padding: '14px 18px' }}>
      <div style={{ fontSize: 14, fontWeight: 800, color: '#0a1628', marginBottom: 2 }}>🎤 Transcrever áudio</div>
      <div style={{ fontSize: 12, color: '#6b7280', marginBottom: 10 }}>
        Áudio (WhatsApp, reunião…) → transcrição + principais pontos + ações que viram tarefas. Processado NA NOSSA máquina — a voz não sai do servidor. Até 5 min por áudio.
      </div>
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
        <label style={{ background: '#f3f4f6', border: '1px dashed #9ca3af', borderRadius: 6, padding: '7px 14px', fontSize: 12.5, color: '#374151', cursor: 'pointer' }}>
          {arquivo ? `🎵 ${arquivo.name.slice(0, 30)}` : '📁 Escolher áudio'}
          <input type="file" accept="audio/*,.ogg,.opus,.m4a" style={{ display: 'none' }}
            onChange={e => { setArquivo(e.target.files?.[0] || null); setResultado(null); setErro(''); }} />
        </label>
        <select value={template} onChange={e => setTemplate(e.target.value)}
          title="O tipo de áudio muda o formato do resumo"
          style={{ padding: '7px 10px', border: '1px solid #c8d0da', borderRadius: 6, fontSize: 12.5, fontWeight: 600, color: '#374151' }}>
          <option value="generico">📝 Resumo geral</option>
          <option value="reuniao_cliente">🤝 Reunião com cliente</option>
          <option value="lead">🎯 Atendimento de lead</option>
          <option value="ata_interna">🏢 Ata de reunião interna</option>
        </select>
        <button onClick={transcrever} disabled={rodando} style={{ ...btn('#0a1628'), opacity: rodando ? 0.6 : 1 }}>
          {rodando ? '⏳ Transcrevendo… (pode levar minutos)' : '🎙️ Transcrever'}
        </button>
      </div>
      {erro && <div style={{ color: '#b91c1c', fontSize: 12.5, marginTop: 8 }}>{erro}</div>}
      {resultado && (
        <div style={{ marginTop: 12 }}>
          {resultado.resumo && (
            <div style={{ background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: 8, padding: '10px 14px', marginBottom: 10 }}>
              <div style={{ fontSize: 12, fontWeight: 800, color: '#1d4ed8', marginBottom: 4 }}>📌 Principais pontos</div>
              <div style={{ fontSize: 13, color: '#1e3a5f', whiteSpace: 'pre-wrap' }}>{resultado.resumo}</div>
            </div>
          )}
          {(resultado.acoes || []).length > 0 && (
            <div style={{ background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: 8, padding: '10px 14px', marginBottom: 10 }}>
              <div style={{ fontSize: 12, fontWeight: 800, color: '#15803d', marginBottom: 6 }}>✅ Ações detectadas — marque e vire tarefas</div>
              {resultado.acoes.map((a, i) => (
                <label key={i} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, color: '#374151', padding: '3px 0', cursor: 'pointer' }}>
                  <input type="checkbox" checked={!!acoesSel[i]} onChange={e => setAcoesSel(s => ({ ...s, [i]: e.target.checked }))} />
                  <span style={{ flex: 1 }}>{a.descricao}</span>
                  <input type="date" value={a.data || ''} title="Data da tarefa (confira — IA pode errar o dia)"
                    onClick={e => e.preventDefault()}
                    onChange={e => setResultado(r => ({ ...r, acoes: r.acoes.map((x, k) => k === i ? { ...x, data: e.target.value } : x) }))}
                    style={{ border: '1px solid #fde68a', background: '#fffbeb', borderRadius: 6, padding: '2px 6px', fontSize: 11.5, color: '#b45309', fontWeight: 700 }} />
                </label>
              ))}
              <button disabled={criandoTarefas} onClick={criarTarefas} style={{ ...btn('#15803d'), marginTop: 6 }}>
                {criandoTarefas ? '⏳ Criando…' : `📅 Criar tarefa(s) na Agenda`}
              </button>
            </div>
          )}
          <div style={{ background: '#f8fafc', border: '1px solid #e5e7eb', borderRadius: 8, padding: '10px 14px', maxHeight: 240, overflowY: 'auto' }}>
            <div style={{ fontSize: 12, fontWeight: 800, color: '#0a1628', marginBottom: 4 }}>📝 Transcrição completa</div>
            <div style={{ fontSize: 13, color: '#374151', whiteSpace: 'pre-wrap' }}>{resultado.transcricao}</div>
          </div>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center', marginTop: 10 }}>
            <button onClick={() => navigator.clipboard.writeText(`📌 ${resultado.resumo}\n\n📝 ${resultado.transcricao}`).then(() => alert('Copiado!'))} style={btn('#f3f4f6', '#374151')}>📋 Copiar</button>
            <button disabled={!!exportando} onClick={() => exportar('pdf')} style={btn('#b91c1c')}>{exportando === 'pdf' ? '⏳' : '📄 PDF'}</button>
            <button disabled={!!exportando} onClick={() => exportar('docx')} style={btn('#1d4ed8')}>{exportando === 'docx' ? '⏳' : '📝 Word'}</button>
            <button disabled={!!exportando} onClick={() => exportar('pdf', 'wa')} style={btn('#16a34a')}>{exportando === 'pdfwa' ? '⏳' : '📲 PDF no WhatsApp'}</button>
          </div>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center', marginTop: 10, background: '#fafafa', border: '1px solid #e5e7eb', borderRadius: 8, padding: '8px 12px' }}>
            <span style={{ fontSize: 12, fontWeight: 700, color: '#374151' }}>👤 Vincular a um cliente:</span>
            {clienteSel ? (
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, background: '#eff6ff', border: '1px solid #bfdbfe', color: '#1d4ed8', borderRadius: 12, padding: '2px 10px', fontSize: 12, fontWeight: 700 }}>
                {clienteSel.nome}
                <span onClick={() => { setClienteSel(null); setBuscaCli(''); }} style={{ cursor: 'pointer', fontWeight: 800 }}>×</span>
              </span>
            ) : (
              <span style={{ position: 'relative' }}>
                <input value={buscaCli} onChange={e => setBuscaCli(e.target.value)} placeholder="buscar cliente…"
                  style={{ padding: '5px 10px', border: '1px solid #c8d0da', borderRadius: 6, fontSize: 12.5, width: 200 }} />
                {opcoesCli.length > 0 && (
                  <span style={{ position: 'absolute', top: '110%', left: 0, background: '#fff', border: '1px solid #dbe2ea', borderRadius: 8, boxShadow: '0 8px 24px rgba(0,0,0,0.12)', zIndex: 50, minWidth: 240, display: 'block' }}>
                    {opcoesCli.map(c => (
                      <span key={c._id} onClick={() => { setClienteSel(c); setOpcoesCli([]); }}
                        style={{ display: 'block', padding: '6px 12px', fontSize: 12.5, color: '#0a1628', cursor: 'pointer' }}
                        onMouseEnter={e => e.currentTarget.style.background = '#f0f4ff'}
                        onMouseLeave={e => e.currentTarget.style.background = '#fff'}>
                        {c.nome}
                      </span>
                    ))}
                  </span>
                )}
              </span>
            )}
            {clienteSel && (
              <button disabled={!!exportando} onClick={() => exportar('pdf', 'cliente')} style={btn('#7c3aed')}>
                {exportando === 'pdfcliente' ? '⏳' : '💾 Salvar PDF no cadastro'}
              </button>
            )}
            <span style={{ fontSize: 11, color: '#9ca3af' }}>tarefas criadas também ficam vinculadas</span>
          </div>
        </div>
      )}
    </div>
  );
}

export default function Utilitarios() {
  const ehVagner = localStorage.getItem('nomeUsuario') === 'Vagner Freitas';
  return (
    <div style={{ maxWidth: 900 }}>
      <div style={{ marginBottom: 16, display: 'flex', alignItems: 'center', gap: 12 }}>
        <div style={{ width: 44, height: 44, borderRadius: 12, background: 'linear-gradient(135deg, #0a1628, #1e3a5f)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 22, boxShadow: '0 2px 6px rgba(10,22,40,.25)' }}>🧰</div>
        <div>
          <h1 style={{ fontSize: 18, fontWeight: 800, color: '#0a1628', margin: 0 }}>Utilitários</h1>
          <p style={{ color: '#6b7280', fontSize: 12.5, margin: '1px 0 0' }}>Ferramentinhas do dia a dia — rápidas, sem cadastro, sem burocracia.</p>
        </div>
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        <TranscreverAudio />
        {ehVagner && <CardConsumoIA />}
        {ehVagner && <CardAnotacoes />}
        <ContadorPrazo />
        <SoNumeros />
      </div>
    </div>
  );
}
